import React from 'react';
import { useTheme } from '@mui/material/styles';
import {
  hasTeaserBeenShown,
  hasTeaserBeenDismissed,
  markTeaserShown,
  markTeaserDismissed,
} from '../hooks/useLauncherTeaserSession';
import { resolveLauncherTeaserMessage } from '../utils/resolveLauncherTeaserMessage';
import { getLauncherTeaserMode } from '../utils/getLauncherTeaserMode';
import { trackWidgetEvent } from '../utils/analytics';
import { launcherTeaserFabRingSx } from '@/lib/launcher-teaser-styles';
import type { WidgetConfig } from '../hooks/useFetchWidgetConfig';

export function useChatFloatingButtonTeaser(config: WidgetConfig['customization']) {
  const theme = useTheme();
  const teaserMode = getLauncherTeaserMode(config);
  const teaserEnabled = Boolean(config.launcherTeaserEnabled);

  // Local flag so dismiss/open re-renders after sessionStorage writes (not set in effects).
  const [isDismissed, setIsDismissed] = React.useState(() => hasTeaserBeenDismissed());

  const showTeaser = teaserEnabled && !isDismissed;

  React.useEffect(() => {
    if (showTeaser && !hasTeaserBeenShown()) {
      markTeaserShown();
    }
  }, [showTeaser]);

  const teaserMessage =
    showTeaser && config.launcherTeaserStyle === 'message'
      ? resolveLauncherTeaserMessage(
          typeof window !== 'undefined' ? window.location.href : '',
          config.launcherTeaserDefaultMessage ?? 'Anything we can help with?',
          config.launcherTeaserPageOverrides ?? [],
        )
      : '';

  const fabPulseSx =
    showTeaser && config.launcherTeaserStyle === 'pulse'
      ? launcherTeaserFabRingSx(theme.palette.primary.main)
      : {};

  const handleTeaserDismiss = () => {
    markTeaserDismissed();
    setIsDismissed(true);
    trackWidgetEvent('Chat Widget - Launcher Teaser Dismissed', { teaserMode: 'message' });
  };

  /** Session dismiss when visitor opens chat via launcher or teaser (not the X button). */
  const dismissTeaserOnOpen = () => {
    markTeaserDismissed();
    setIsDismissed(true);
  };

  return {
    teaserMode,
    showTeaser,
    teaserMessage,
    fabPulseSx,
    handleTeaserDismiss,
    dismissTeaserOnOpen,
  };
}
